import dayjs from "dayjs";
import { ResolvedTime } from "resolvers/data/siteResolver";

export type PhoneLinesInfo = {
    onlinePhoneLinesText: string;
    isClosed: boolean;
};

type DateFormattedOpeningTimes = {
    todaysOpeningTimeDateFormatted: Date;
    todaysClosingTimeDateFormatted: Date;
    nextOpeningTimeDateFormatted: Date;
};

type StringFormattedOpeningTimes = {
    todaysOpeningTimeStringFormatted: string;
    todaysClosingTimeStringFormatted: string;
    nextOpeningTimeStringFormatted: string;
    nextOpeningTimeDayStringFormatted: string;
};

type ContentOpeningTimes = {
    todaysOpeningTime: string;
    todaysClosingTime: string;
    nextOpeningTime: string;
};

type OnlineOpeningValues = {
    isClosed: boolean;
    afterClosingTime: boolean;
};

const fallbackOpeningTimes: Record<string, ResolvedTime> = {
    weekday: {
        isOpen: true,
        openingTime: "08:30:00",
        closingTime: "20:00:00",
        date: new Date(),
        day: "Weekday",
    },
    saturday: {
        isOpen: true,
        openingTime: "09:00:00",
        closingTime: "18:00:00",
        date: new Date(),
        day: "Saturday",
    },
    sunday: {
        isOpen: true,
        openingTime: "10:00:00",
        closingTime: "17:00:00",
        date: new Date(),
        day: "Sunday",
    },
};

const getFallback = (targetDay: "today" | "next"): ResolvedTime => {
    const currentDay = dayjs().day(); // Sunday = 0, ..., Saturday = 6
    let fallbackDay: number = 0;

    if (targetDay === "today") {
        fallbackDay = currentDay;
    } else if (targetDay === "next") {
        fallbackDay = (currentDay + 1) % 7; // Move to the next day, wrap around with % 7
    }

    if (fallbackDay === 0) return fallbackOpeningTimes.sunday; // Sunday
    if (fallbackDay === 6) return fallbackOpeningTimes.saturday; // Saturday
    return fallbackOpeningTimes.weekday; // Weekday
};

const isOnlineClosed = (
    dateFormattedOpeningTimes: DateFormattedOpeningTimes,
    currentTime: Date
): OnlineOpeningValues => {
    let beforeOpenTime = dateFormattedOpeningTimes.todaysOpeningTimeDateFormatted > currentTime;
    let afterClosingTime = dateFormattedOpeningTimes.todaysClosingTimeDateFormatted < currentTime;

    return {
        isClosed: beforeOpenTime || afterClosingTime,
        afterClosingTime: afterClosingTime,
    };
};

const getDateFormattedOpeningTimes = (
    contentOpeningTimes: ContentOpeningTimes,
    currentTime: Date
): DateFormattedOpeningTimes => {
    let todaysContructedOpeningTime = new Date(
        currentTime.toDateString() + " " + contentOpeningTimes.todaysOpeningTime
    );
    let todaysContructedClosingTime = new Date(
        currentTime.toDateString() + " " + contentOpeningTimes.todaysClosingTime
    );
    let nextOpenContructedOpeningTime = new Date(
        currentTime.toDateString() + " " + contentOpeningTimes.nextOpeningTime
    );

    return {
        todaysOpeningTimeDateFormatted: todaysContructedOpeningTime,
        todaysClosingTimeDateFormatted: todaysContructedClosingTime,
        nextOpeningTimeDateFormatted: nextOpenContructedOpeningTime,
    };
};

const getStringFormattedOpeningTimes = (
    dates: DateFormattedOpeningTimes,
    openingTimes: ContentOpeningTimes,
    nextOpenHours: ResolvedTime | undefined
): StringFormattedOpeningTimes => {
    const formatDate = (date: Date) => dayjs(date).format("ddd");
    const formatTime = (time: string, format: string) => dayjs(time, "HH:mm:ss").format(format);

    const todaysOpeningTimeFormatted =
        dates.todaysOpeningTimeDateFormatted.getMinutes() > 0
            ? formatTime(openingTimes.todaysOpeningTime, "h:mmA").toLowerCase()
            : formatTime(openingTimes.todaysOpeningTime, "hA").toLowerCase();
    const todaysClosingTimeFormatted =
        dates.todaysClosingTimeDateFormatted.getMinutes() > 0
            ? formatTime(openingTimes.todaysClosingTime, "h:mmA").toLowerCase()
            : formatTime(openingTimes.todaysClosingTime, "hA").toLowerCase();

    const nextOpeningTimeFormatted =
        dates.nextOpeningTimeDateFormatted.getMinutes() > 0
            ? formatTime(openingTimes.nextOpeningTime, "h:mmA").toLowerCase()
            : formatTime(openingTimes.nextOpeningTime, "hA").toLowerCase();

    const nextOpeningTimeDayFormatted = `${
        !!nextOpenHours ? formatDate(nextOpenHours?.date) : ""
    } ${nextOpeningTimeFormatted}`;

    return {
        todaysOpeningTimeStringFormatted: todaysOpeningTimeFormatted,
        todaysClosingTimeStringFormatted: todaysClosingTimeFormatted,
        nextOpeningTimeStringFormatted: nextOpeningTimeFormatted,
        nextOpeningTimeDayStringFormatted: nextOpeningTimeDayFormatted,
    };
};

const getOnlinePhoneLinesText = (
    resolvedOpeningTimes: ResolvedTime[],
    phoneLinesOpenText: string,
    phoneLinesClosedText: string
): PhoneLinesInfo => {
    const todaysOpenHours = resolvedOpeningTimes.find(({ date }: any) => dayjs(date).isToday());
    const nextOpenHours = resolvedOpeningTimes.find(({ date }: any) =>
        dayjs(date).isAfter(dayjs(), "day")
    );

    const finalTodaysOpenHours = todaysOpenHours ?? getFallback("today");
    const finalNextOpenHours = nextOpenHours ?? getFallback("next");

    const contentOpeningTimes: ContentOpeningTimes = {
        todaysOpeningTime: finalTodaysOpenHours.openingTime,
        todaysClosingTime: finalTodaysOpenHours.closingTime,
        nextOpeningTime: finalNextOpenHours.openingTime,
    };

    const currentTime = new Date();

    const dateFormattedOpeningTimes = getDateFormattedOpeningTimes(
        contentOpeningTimes,
        currentTime
    );
    const {
        todaysOpeningTimeStringFormatted,
        todaysClosingTimeStringFormatted,
        nextOpeningTimeDayStringFormatted,
    } = getStringFormattedOpeningTimes(
        dateFormattedOpeningTimes,
        contentOpeningTimes,
        nextOpenHours
    );

    const { isClosed, afterClosingTime } = isOnlineClosed(dateFormattedOpeningTimes, currentTime);

    const phoneLinesText = getformattedText(
        isClosed,
        afterClosingTime,
        phoneLinesOpenText,
        phoneLinesClosedText,
        nextOpeningTimeDayStringFormatted,
        todaysClosingTimeStringFormatted,
        todaysOpeningTimeStringFormatted
    );

    return {
        onlinePhoneLinesText: phoneLinesText,
        isClosed: isClosed,
    };
};

const getformattedText = (
    isClosed: boolean,
    afterClosingTime: boolean,
    phoneLinesOpenText: string,
    phoneLinesClosedText: string,
    nextOpeningTimeDayFormatted: string,
    todaysClosingTimeFormatted: string,
    todaysOpeningTimeFormatted: string
) => {
    if (isClosed) {
        if (afterClosingTime) {
            return phoneLinesClosedText.replaceAll("{openingTime}", nextOpeningTimeDayFormatted);
        } else {
            return phoneLinesClosedText.replaceAll("{openingTime}", todaysOpeningTimeFormatted);
        }
    } else {
        return phoneLinesOpenText.replaceAll("{closingTime}", todaysClosingTimeFormatted);
    }
};

export default getOnlinePhoneLinesText;
